// ── Installer Execution Strictness ──
// Platform-level setting governing how strictly the installer surface
// enforces phase gates, pre-install edits, and geo-deviation handling.
//
// Modes:
//   • velocity  — current behavior. Soft-submit on pre-install phases.
//                 Future phases visible as previews. Distance logged only.
//   • standard  — DEFAULT. Soft-submit on pre-install phases (crew velocity
//                 preserved). Future phases hidden until current is submitted.
//                 Distance >150m surfaces a soft warning to the installer.
//   • strict    — Hard gate every phase: no edits after submit. Future phases
//                 hidden. Distance >150m blocks submit until a typed reason
//                 is provided. Out-of-window check-in requires typed reason.
//
// Always-on (mode-independent):
//   • Location permission required to use the app (one-time gate).
//   • Coords + haversine distance silently captured on every check-in.
//   • Distance to job address shown quietly on check-in confirmation.
//   • Capture outcomes feed backend trust scoring (recorded on event).

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { UserRole } from "./RoleContext";
import type { DeviationEvent } from "./installerGeo";
import type {
  ProofPolicy,
  ProofPolicyAuditEntry,
  ProofGapEvent,
} from "./installerProof";

export type InstallerStrictness = "velocity" | "standard" | "strict";

export interface InstallerStrictnessAuditEntry {
  id: string;
  changedAt: string; // ISO
  changedBy: UserRole;
  from: InstallerStrictness;
  to: InstallerStrictness;
  reason?: string;
}

interface InstallerExecutionContextValue {
  strictness: InstallerStrictness;
  setStrictness: (next: InstallerStrictness, opts: { changedBy: UserRole; reason?: string }) => void;
  auditLog: InstallerStrictnessAuditEntry[];
  /** Layer 2 — rolling check-in deviation log (capped at 50). */
  deviationEvents: DeviationEvent[];
  recordDeviation: (event: DeviationEvent) => void;
  clearDeviationEvents: () => void;
  /** Layer 3a — proof enforcement policy (independent of strictness). */
  proofPolicy: ProofPolicy;
  setProofPolicy: (next: ProofPolicy, opts: { changedBy: UserRole; reason?: string }) => void;
  proofPolicyAudit: ProofPolicyAuditEntry[];
  proofGapEvents: ProofGapEvent[];
  recordProofGap: (event: ProofGapEvent) => void;
  clearProofGapEvents: () => void;
  /** Behavioral derivations (single source of truth) */
  allowsSoftSubmitPreInstall: boolean;
  hidesFuturePhases: boolean;
  hardGateAllPhases: boolean;
  requiresReasonOnDistanceDeviation: boolean;
  requiresReasonOnOutOfWindow: boolean;
  /** Distance threshold (meters) at which proximity becomes a deviation. */
  distanceDeviationThresholdMeters: number;
}

const STORAGE_KEY = "installer:executionStrictness";
const AUDIT_KEY = "installer:executionStrictness:audit";
const DEVIATION_KEY = "installer:executionStrictness:deviations";
const PROOF_POLICY_KEY = "installer:proofPolicy";
const PROOF_AUDIT_KEY = "installer:proofPolicy:audit";
const PROOF_GAP_KEY = "installer:proofPolicy:gaps";
const DEVIATION_CAP = 50;
const PROOF_GAP_CAP = 50;
const PROOF_AUDIT_CAP = 100;

/** Roles authorized to change this setting. */
export const STRICTNESS_EDITORS: UserRole[] = [
  "super_admin",
  "owner",
  "admin",
  "ops_manager",
  "head_of_installation",
  "it_staff",
];

export function canEditInstallerStrictness(role: UserRole): boolean {
  return STRICTNESS_EDITORS.includes(role);
}

const InstallerExecutionContext = createContext<InstallerExecutionContextValue | null>(null);

function deriveBehavior(mode: InstallerStrictness) {
  switch (mode) {
    case "velocity":
      return {
        allowsSoftSubmitPreInstall: true,
        hidesFuturePhases: false,
        hardGateAllPhases: false,
        requiresReasonOnDistanceDeviation: false,
        requiresReasonOnOutOfWindow: false,
      };
    case "strict":
      return {
        allowsSoftSubmitPreInstall: false,
        hidesFuturePhases: true,
        hardGateAllPhases: true,
        requiresReasonOnDistanceDeviation: true,
        requiresReasonOnOutOfWindow: true,
      };
    case "standard":
    default:
      return {
        allowsSoftSubmitPreInstall: true,
        hidesFuturePhases: true,
        hardGateAllPhases: false,
        requiresReasonOnDistanceDeviation: false,
        requiresReasonOnOutOfWindow: false,
      };
  }
}

export function InstallerExecutionProvider({ children }: { children: ReactNode }) {
  const [strictness, setStrictnessState] = useState<InstallerStrictness>(() => {
    if (typeof window === "undefined") return "standard";
    const raw = window.localStorage.getItem(STORAGE_KEY) as InstallerStrictness | null;
    if (raw === "velocity" || raw === "standard" || raw === "strict") return raw;
    return "standard";
  });

  const [auditLog, setAuditLog] = useState<InstallerStrictnessAuditEntry[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(AUDIT_KEY);
      return raw ? (JSON.parse(raw) as InstallerStrictnessAuditEntry[]) : [];
    } catch {
      return [];
    }
  });

  const [deviationEvents, setDeviationEvents] = useState<DeviationEvent[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(DEVIATION_KEY);
      return raw ? (JSON.parse(raw) as DeviationEvent[]) : [];
    } catch {
      return [];
    }
  });

  const [proofPolicy, setProofPolicyState] = useState<ProofPolicy>(() => {
    if (typeof window === "undefined") return "strict_proof";
    const raw = window.localStorage.getItem(PROOF_POLICY_KEY) as ProofPolicy | null;
    if (
      raw === "strict_proof" ||
      raw === "checklist_only" ||
      raw === "photo_only" ||
      raw === "advisory"
    ) return raw;
    return "strict_proof";
  });

  const [proofPolicyAudit, setProofPolicyAudit] = useState<ProofPolicyAuditEntry[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(PROOF_AUDIT_KEY);
      return raw ? (JSON.parse(raw) as ProofPolicyAuditEntry[]) : [];
    } catch {
      return [];
    }
  });

  const [proofGapEvents, setProofGapEvents] = useState<ProofGapEvent[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(PROOF_GAP_KEY);
      return raw ? (JSON.parse(raw) as ProofGapEvent[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, strictness);
  }, [strictness]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(AUDIT_KEY, JSON.stringify(auditLog));
  }, [auditLog]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(DEVIATION_KEY, JSON.stringify(deviationEvents));
  }, [deviationEvents]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(PROOF_POLICY_KEY, proofPolicy);
  }, [proofPolicy]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(PROOF_AUDIT_KEY, JSON.stringify(proofPolicyAudit));
  }, [proofPolicyAudit]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(PROOF_GAP_KEY, JSON.stringify(proofGapEvents));
  }, [proofGapEvents]);

  const setStrictness: InstallerExecutionContextValue["setStrictness"] = (next, opts) => {
    if (next === strictness) return;
    const entry: InstallerStrictnessAuditEntry = {
      id: `iesa_${Date.now()}`,
      changedAt: new Date().toISOString(),
      changedBy: opts.changedBy,
      from: strictness,
      to: next,
      reason: opts.reason,
    };
    setAuditLog((prev) => [entry, ...prev].slice(0, 100));
    setStrictnessState(next);
  };

  const recordDeviation: InstallerExecutionContextValue["recordDeviation"] = (event) => {
    setDeviationEvents((prev) => {
      // Idempotency guard: collapse if same id already present.
      if (prev.some((e) => e.id === event.id)) return prev;
      return [event, ...prev].slice(0, DEVIATION_CAP);
    });
  };

  const clearDeviationEvents = () => setDeviationEvents([]);

  const setProofPolicy: InstallerExecutionContextValue["setProofPolicy"] = (next, opts) => {
    if (next === proofPolicy) return;
    const entry: ProofPolicyAuditEntry = {
      id: `ppa_${Date.now()}`,
      changedAt: new Date().toISOString(),
      changedBy: opts.changedBy,
      from: proofPolicy,
      to: next,
      reason: opts.reason,
    };
    setProofPolicyAudit((prev) => [entry, ...prev].slice(0, PROOF_AUDIT_CAP));
    setProofPolicyState(next);
  };

  const recordProofGap: InstallerExecutionContextValue["recordProofGap"] = (event) => {
    setProofGapEvents((prev) => {
      if (prev.some((e) => e.id === event.id)) return prev;
      return [event, ...prev].slice(0, PROOF_GAP_CAP);
    });
  };

  const clearProofGapEvents = () => setProofGapEvents([]);

  const behavior = deriveBehavior(strictness);

  return (
    <InstallerExecutionContext.Provider
      value={{
        strictness,
        setStrictness,
        auditLog,
        deviationEvents,
        recordDeviation,
        clearDeviationEvents,
        proofPolicy,
        setProofPolicy,
        proofPolicyAudit,
        proofGapEvents,
        recordProofGap,
        clearProofGapEvents,
        ...behavior,
        distanceDeviationThresholdMeters: 150,
      }}
    >
      {children}
    </InstallerExecutionContext.Provider>
  );
}

export function useInstallerExecution(): InstallerExecutionContextValue {
  const ctx = useContext(InstallerExecutionContext);
  if (!ctx) {
    // Safe fallback for code paths that render outside the provider during
    // tests or storybook. Defaults to Standard behavior.
    const behavior = deriveBehavior("standard");
    return {
      strictness: "standard",
      setStrictness: () => {},
      auditLog: [],
      deviationEvents: [],
      recordDeviation: () => {},
      clearDeviationEvents: () => {},
      proofPolicy: "strict_proof",
      setProofPolicy: () => {},
      proofPolicyAudit: [],
      proofGapEvents: [],
      recordProofGap: () => {},
      clearProofGapEvents: () => {},
      ...behavior,
      distanceDeviationThresholdMeters: 150,
    };
  }
  return ctx;
}

export const STRICTNESS_LABEL: Record<InstallerStrictness, string> = {
  velocity: "Velocity",
  standard: "Standard",
  strict: "Strict",
};

export const STRICTNESS_DESCRIPTION: Record<InstallerStrictness, string> = {
  velocity:
    "Crews can revise pre-install phases after submitting and preview upcoming phases. Distance deviations are logged silently for trust scoring.",
  standard:
    "Crews can revise pre-install phases after submitting, but future phases stay hidden until the current one is submitted. Distance deviations surface a quiet on-screen note. Recommended default.",
  strict:
    "Every phase is a hard gate — no edits after submit. Future phases are hidden. Check-ins more than 150m from the job address or outside the window require a typed reason.",
};
